import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, Utensils, Calendar, ChevronLeft, ChevronRight, Coffee, Sun, Moon, Apple, History, Plus, Trash2, Globe, ChefHat, Leaf, ListOrdered, Info } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Badge } from './ui/badge';

const SmartMealPlanner = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [generating, setGenerating] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [savedPlans, setSavedPlans] = useState([]);
  const [activeDay, setActiveDay] = useState(0);
  const [viewMode, setViewMode] = useState('create'); // 'create', 'view', 'history'
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingCuisines, setLoadingCuisines] = useState(false);
  const [availableCuisines, setAvailableCuisines] = useState([]);
  const [expandedMeal, setExpandedMeal] = useState(null);
  const [preferences, setPreferences] = useState({
    dietary_type: 'balanced',
    target_calories: 2000,
    goal: 'maintenance',
    cuisine: '',
    restrictions: []
  });

  const dietaryTypes = [
    { value: 'balanced', label: 'Balanced', icon: '🍽️' },
    { value: 'vegan', label: 'Vegan', icon: '🌱' },
    { value: 'vegetarian', label: 'Vegetarian', icon: '🥬' },
    { value: 'keto', label: 'Keto', icon: '🥑' },
    { value: 'paleo', label: 'Paleo', icon: '🍖' },
    { value: 'gluten_free', label: 'Gluten-Free', icon: '🌾' },
    { value: 'dairy_free', label: 'Dairy-Free', icon: '🥛' }
  ];

  const goals = [
    { value: 'weight_loss', label: 'Weight Loss', icon: '📉' },
    { value: 'muscle_gain', label: 'Muscle Gain', icon: '💪' },
    { value: 'maintenance', label: 'Maintenance', icon: '⚖️' },
    { value: 'health', label: 'General Health', icon: '❤️' }
  ];

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Cuisine groups for better organization
  const cuisineGroups = {
    'South Asia': ['indian_north', 'indian_south'],
    'East Asia': ['chinese', 'japanese', 'korean'],
    'Southeast Asia': ['thai', 'filipino'],
    'Middle East': ['middle_eastern'],
    'Mediterranean': ['mediterranean'],
    'Africa': ['african'],
    'Latin America': ['latin_american'],
    'Western': ['western'],
    'Special Diets': ['vegan_global', 'vegetarian_global']
  };

  // Load cuisines and saved meal plans on mount
  useEffect(() => {
    if (isOpen) {
      loadCuisines();
      loadSavedPlans();
    }
  }, [isOpen]);

  const loadCuisines = async () => {
    setLoadingCuisines(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/health/cuisines`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setAvailableCuisines(data.cuisines || []);
        
        // Try to auto-detect cuisine from user's locale
        const userLocale = navigator.language?.split('-')[1] || 'US';
        if (data.country_mapping && data.country_mapping[userLocale]) {
          setPreferences(prev => ({
            ...prev,
            cuisine: data.country_mapping[userLocale]
          }));
        }
      }
    } catch (error) {
      console.error('Error loading cuisines:', error);
    } finally {
      setLoadingCuisines(false);
    }
  };

  const loadSavedPlans = async () => {
    setLoadingHistory(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/health/meal-plans`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setSavedPlans(data.meal_plans || []);
        // If there's a recent plan, show it
        if (data.meal_plans && data.meal_plans.length > 0) {
          const latestPlan = data.meal_plans[0];
          if (latestPlan.structured_plan) {
            setCurrentPlan(latestPlan);
            setViewMode('view');
          }
        }
      }
    } catch (error) {
      console.error('Error loading meal plans:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/health/meal-plan/generate-structured`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(preferences)
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCurrentPlan(data);
        setViewMode('view');
        setActiveDay(0);
        toast({
          title: '✅ Meal Plan Generated!',
          description: `Your personalized ${data.cuisine_name || 'global'} 7-day meal plan is ready`
        });
        // Reload saved plans
        loadSavedPlans();
      } else {
        throw new Error('Failed to generate meal plan');
      }
    } catch (error) {
      console.error('Error generating meal plan:', error);
      toast({
        title: 'Generation Failed',
        description: 'Unable to generate meal plan. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setGenerating(false);
    }
  };

  const deletePlan = async (planId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/health/meal-plans/${planId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (response.ok) {
        toast({ title: 'Meal plan deleted' });
        loadSavedPlans();
        if (currentPlan?.id === planId) {
          setCurrentPlan(null);
          setViewMode('create');
        }
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
    }
  };

  const renderMealCard = (meal, mealType, icon, mealKey) => {
    const isExpanded = expandedMeal === mealKey;
    const hasDetails = meal?.ingredients?.length > 0 || meal?.prep_steps?.length > 0;
    
    const toggleExpand = () => {
      if (hasDetails) {
        setExpandedMeal(isExpanded ? null : mealKey);
      }
    };
    
    return (
      <Card 
        className={`border-l-4 ${mealType === 'Breakfast' ? 'border-l-orange-400' : mealType === 'Lunch' ? 'border-l-yellow-400' : 'border-l-indigo-400'} hover:shadow-md transition-shadow ${hasDetails ? 'cursor-pointer' : ''}`}
        onClick={toggleExpand}
      >
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            {icon}
            {mealType}
            {meal?.calories && (
              <Badge variant="outline" className="ml-auto text-xs">
                {meal.calories} cal
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="py-2 px-4">
          <p className="font-semibold text-gray-900">{meal?.name || 'Not specified'}</p>
          {meal?.description && (
            <p className="text-sm text-gray-600 mt-1">{meal.description}</p>
          )}
          
          {/* Nutrition Info */}
          {meal?.nutrition && (
            <div className="flex flex-wrap gap-2 mt-2">
              {meal.nutrition.protein && (
                <Badge variant="secondary" className="text-xs">🥩 {meal.nutrition.protein}</Badge>
              )}
              {meal.nutrition.carbs && (
                <Badge variant="secondary" className="text-xs">🍞 {meal.nutrition.carbs}</Badge>
              )}
              {meal.nutrition.fat && (
                <Badge variant="secondary" className="text-xs">🧈 {meal.nutrition.fat}</Badge>
              )}
              {meal.nutrition.fiber && (
                <Badge variant="secondary" className="text-xs">🥬 {meal.nutrition.fiber}</Badge>
              )}
            </div>
          )}
          
          {/* Expandable section for ingredients and prep steps */}
          {isExpanded && (
            <div className="mt-3 pt-3 border-t space-y-3 animate-in slide-in-from-top-2">
              {meal?.ingredients && meal.ingredients.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 font-semibold flex items-center gap-1 mb-1">
                    <Leaf className="h-3 w-3" /> Ingredients:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {meal.ingredients.map((ing, i) => (
                      <Badge key={i} variant="outline" className="text-xs bg-green-50">
                        {ing}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {meal?.prep_steps && meal.prep_steps.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 font-semibold flex items-center gap-1 mb-1">
                    <ListOrdered className="h-3 w-3" /> Preparation Steps:
                  </p>
                  <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                    {meal.prep_steps.map((step, i) => (
                      <li key={i} className="leading-relaxed">{step}</li>
                    ))}
                  </ol>
                </div>
              )}
              
              {/* Click to collapse hint */}
              <p className="text-xs text-gray-400 mt-2 flex items-center gap-1 justify-center">
                <Info className="h-3 w-3" /> Click to collapse
              </p>
            </div>
          )}
          
          {/* Click to expand hint */}
          {hasDetails && !isExpanded && (
            <p className="text-xs text-teal-600 mt-2 flex items-center gap-1 hover:text-teal-800">
              <Info className="h-3 w-3" /> Click for ingredients & recipe
            </p>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderDayPlan = (dayPlan, dayIndex) => {
    if (!dayPlan) return <p className="text-gray-500">No plan for this day</p>;
    
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-gray-900">{dayNames[dayIndex]}</h3>
          {dayPlan.total_calories && (
            <Badge className="bg-green-100 text-green-800">
              {dayPlan.total_calories} total calories
            </Badge>
          )}
        </div>
        
        <div className="space-y-3">
          {renderMealCard(dayPlan.breakfast, 'Breakfast', <Coffee className="h-4 w-4 text-orange-500" />, `${dayIndex}-breakfast`)}
          {renderMealCard(dayPlan.lunch, 'Lunch', <Sun className="h-4 w-4 text-yellow-500" />, `${dayIndex}-lunch`)}
          {renderMealCard(dayPlan.dinner, 'Dinner', <Moon className="h-4 w-4 text-indigo-500" />, `${dayIndex}-dinner`)}
          {dayPlan.snacks && dayPlan.snacks.length > 0 && (
            <Card className="border-l-4 border-l-pink-400">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Apple className="h-4 w-4 text-red-500" />
                  Snacks
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2 px-4">
                {dayPlan.snacks.map((snack, i) => (
                  <p key={i} className="text-sm text-gray-700">• {snack}</p>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  };

  const getCuisineIcon = (cuisineValue) => {
    const icons = {
      'indian_north': '🇮🇳',
      'indian_south': '🇮🇳',
      'chinese': '🇨🇳',
      'japanese': '🇯🇵',
      'korean': '🇰🇷',
      'thai': '🇹🇭',
      'filipino': '🇵🇭',
      'middle_eastern': '🕌',
      'mediterranean': '🏛️',
      'african': '🌍',
      'latin_american': '🌮',
      'western': '🍔',
      'vegan_global': '🌱',
      'vegetarian_global': '🥗'
    };
    return icons[cuisineValue] || '🍽️';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ChefHat className="h-5 w-5 text-green-600" />
            Global Meal Planner
            <Badge variant="outline" className="ml-2 text-xs">
              <Globe className="h-3 w-3 mr-1" />
              12+ Cuisines
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Navigation Tabs */}
        <div className="flex gap-2 border-b pb-2">
          <Button
            variant={viewMode === 'create' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('create')}
          >
            <Plus className="h-4 w-4 mr-1" />
            New Plan
          </Button>
          <Button
            variant={viewMode === 'view' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('view')}
            disabled={!currentPlan}
          >
            <Calendar className="h-4 w-4 mr-1" />
            Current Plan
          </Button>
          <Button
            variant={viewMode === 'history' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('history')}
          >
            <History className="h-4 w-4 mr-1" />
            History ({savedPlans.length})
          </Button>
        </div>

        {/* Create New Plan View */}
        {viewMode === 'create' && (
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Generate a personalized 7-day meal plan with cuisine from around the world 🌍
            </p>

            {/* Cuisine Selection - Most Important */}
            <div className="space-y-2 p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border border-teal-100">
              <Label className="flex items-center gap-2 text-teal-800 font-semibold">
                <Globe className="h-4 w-4" />
                Select Your Cuisine
              </Label>
              <Select
                value={preferences.cuisine}
                onValueChange={(value) =>
                  setPreferences({ ...preferences, cuisine: value })
                }
              >
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Choose a cuisine style...">
                    {preferences.cuisine && (
                      <span className="flex items-center gap-2">
                        {getCuisineIcon(preferences.cuisine)}
                        {availableCuisines.find(c => c.value === preferences.cuisine)?.label || preferences.cuisine}
                      </span>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {Object.entries(cuisineGroups).map(([region, cuisineKeys]) => (
                    <div key={region}>
                      <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50">
                        {region}
                      </div>
                      {cuisineKeys.map((key) => {
                        const cuisine = availableCuisines.find(c => c.value === key);
                        if (!cuisine) return null;
                        return (
                          <SelectItem key={key} value={key}>
                            <span className="flex items-center gap-2">
                              {getCuisineIcon(key)} {cuisine.label}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </div>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-teal-600">
                Auto-detected based on your region, but you can change it anytime
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dietary Type</Label>
                <Select
                  value={preferences.dietary_type}
                  onValueChange={(value) =>
                    setPreferences({ ...preferences, dietary_type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {dietaryTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <span className="flex items-center gap-2">
                          {type.icon} {type.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Goal</Label>
                <Select
                  value={preferences.goal}
                  onValueChange={(value) =>
                    setPreferences({ ...preferences, goal: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {goals.map((goal) => (
                      <SelectItem key={goal.value} value={goal.value}>
                        <span className="flex items-center gap-2">
                          {goal.icon} {goal.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Daily Calorie Target</Label>
                <Input
                  type="number"
                  value={preferences.target_calories}
                  onChange={(e) =>
                    setPreferences({
                      ...preferences,
                      target_calories: parseInt(e.target.value)
                    })
                  }
                  min="1000"
                  max="5000"
                />
              </div>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={generating || !preferences.cuisine}
              className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating Your Global Meal Plan...
                </>
              ) : (
                <>
                  <ChefHat className="h-4 w-4 mr-2" />
                  Generate 7-Day {preferences.cuisine ? getCuisineIcon(preferences.cuisine) : '🌍'} Meal Plan
                </>
              )}
            </Button>
          </div>
        )}

        {/* View Current Plan */}
        {viewMode === 'view' && currentPlan && (
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                {currentPlan.cuisine_name && (
                  <Badge className="bg-teal-100 text-teal-800">
                    {getCuisineIcon(currentPlan.preferences?.cuisine)} {currentPlan.cuisine_name}
                  </Badge>
                )}
                <Badge className="bg-green-100 text-green-800">
                  {currentPlan.preferences?.dietary_type?.replace('_', ' ').toUpperCase() || 'BALANCED'}
                </Badge>
                <Badge variant="outline">
                  {currentPlan.preferences?.target_calories || 2000} cal/day
                </Badge>
              </div>
              <p className="text-xs text-gray-500">
                Created: {new Date(currentPlan.created_at).toLocaleDateString()}
              </p>
            </div>

            {/* Day Navigation */}
            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveDay(Math.max(0, activeDay - 1))}
                disabled={activeDay === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <div className="flex gap-1 overflow-x-auto">
                {dayNames.map((day, index) => (
                  <Button
                    key={day}
                    variant={activeDay === index ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveDay(index)}
                    className="text-xs px-2"
                  >
                    {day.slice(0, 3)}
                  </Button>
                ))}
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveDay(Math.min(6, activeDay + 1))}
                disabled={activeDay === 6}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Day Content */}
            <div className="min-h-[300px]">
              {currentPlan.structured_plan ? (
                renderDayPlan(currentPlan.structured_plan[activeDay], activeDay)
              ) : currentPlan.meal_plan ? (
                <div className="bg-white rounded-lg border p-4">
                  <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                    {currentPlan.meal_plan}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No meal plan data available</p>
              )}
            </div>
          </div>
        )}

        {/* History View */}
        {viewMode === 'history' && (
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">Your saved meal plans from around the world</p>
            
            {loadingHistory ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-green-600" />
              </div>
            ) : savedPlans.length === 0 ? (
              <div className="text-center py-8">
                <Utensils className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No saved meal plans yet</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setViewMode('create')}
                >
                  Create Your First Plan
                </Button>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {savedPlans.map((plan) => (
                  <Card key={plan.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="py-3 px-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {plan.cuisine_name && (
                              <Badge className="bg-teal-100 text-teal-800 text-xs">
                                {getCuisineIcon(plan.preferences?.cuisine)} {plan.cuisine_name}
                              </Badge>
                            )}
                            <Badge className="bg-green-100 text-green-800 text-xs">
                              {plan.preferences?.dietary_type?.replace('_', ' ') || 'Balanced'}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {plan.preferences?.target_calories || 2000} cal
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(plan.created_at).toLocaleDateString()} - {plan.preferences?.goal?.replace('_', ' ') || 'General'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setCurrentPlan(plan);
                              setViewMode('view');
                              setActiveDay(0);
                            }}
                          >
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => deletePlan(plan.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SmartMealPlanner;
